import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ICreditsPoolItemStore } from './interfaces';
import { toggleArchiveCreditPool, deleteCreditPool, fetchCreditsPools } from './creditsPools.thunk';
import { fetchCreditPoolById } from '../creditPool/creditPool.thunks';
import { ICreditPool } from '../creditPool/interfaces';

export enum FetchingCreditsPoolStatus {
  Loading = 'loading',
  Succeeded = 'succeeded',
  Failed = 'failed',
  Idle = 'idle'
}

export enum CreditsPoolView {
  Archived = 'archived',
  Active = 'active',
}

type CreditsPoolId = string;
type CreditsPoolItemByIdMap = Record<CreditsPoolId, ICreditsPoolItemStore>;

interface ICreditsPoolsState {
  fetchingCreditsPoolsStatus: FetchingCreditsPoolStatus;
  idsListOfActive: string[];
  idsListOfArchived: string[];
  map: CreditsPoolItemByIdMap;
  currentView: CreditsPoolView;
  isDeleting: boolean;
}

export const initialState: ICreditsPoolsState = {
  fetchingCreditsPoolsStatus: FetchingCreditsPoolStatus.Idle,
  idsListOfActive: [],
  idsListOfArchived: [],
  map: {},
  currentView: CreditsPoolView.Active,
  isDeleting: false
};

const creditsPoolsSlice = createSlice({
  name: 'creditsPools',
  initialState,
  reducers: {
    changeViewToActive: (state) => {
      state.currentView = CreditsPoolView.Active;
    },
    changeViewToArchived: (state) => {
      state.currentView = CreditsPoolView.Archived;
    }
  },
  extraReducers: {
    [fetchCreditsPools.pending.type]: (state) => {
      state.fetchingCreditsPoolsStatus = FetchingCreditsPoolStatus.Loading;
    },
    [fetchCreditsPools.rejected.type]: (state) => {
      state.fetchingCreditsPoolsStatus = FetchingCreditsPoolStatus.Failed;
    },
    [fetchCreditsPools.fulfilled.type]: (state, action: PayloadAction<ICreditsPoolItemStore[]>) => {
      const data = action.payload;
      const dictionaryOfAllCreditsPools: CreditsPoolItemByIdMap = {};
      const idsOfActiveCreditsPools: CreditsPoolId[] = [];
      const idsOfArchivedCreditsPools: CreditsPoolId[] = [];
      data.forEach((creditsPoolItem) => {
        dictionaryOfAllCreditsPools[creditsPoolItem._id] = { ...creditsPoolItem };
        if(creditsPoolItem.isActive) {
          idsOfActiveCreditsPools.push(creditsPoolItem._id);
        }
        if(!creditsPoolItem.isActive) {
          idsOfArchivedCreditsPools.push(creditsPoolItem._id);
        }
      });
      state.map = dictionaryOfAllCreditsPools;
      state.idsListOfActive = idsOfActiveCreditsPools;
      state.idsListOfArchived = idsOfArchivedCreditsPools;
      state.fetchingCreditsPoolsStatus = FetchingCreditsPoolStatus.Idle;
    },
    [deleteCreditPool?.pending.type]: (state) => {
      state.isDeleting = true;
    },
    [deleteCreditPool?.fulfilled.type]: (state, action: PayloadAction<string>) => {
      state.isDeleting = false;
      const id = action.payload;
      delete state.map[id];
      const index = state.idsListOfActive.findIndex((activeId) => activeId === id);
      state.idsListOfActive.splice(index, 1);
    },
    [deleteCreditPool?.rejected.type]: (state) => {
      state.isDeleting = false;
    },
    [toggleArchiveCreditPool?.fulfilled.type]: (state, action: PayloadAction<string>) => {
      const creditPoolId = action.payload;
      const indexOfActiveId = state.idsListOfActive.findIndex((id) => id === creditPoolId);
      const indexOfArchivedId = state.idsListOfArchived.findIndex((id) => id === creditPoolId);
      if(indexOfActiveId >= 0) {
        state.idsListOfActive.splice(indexOfActiveId, 1);
        state.idsListOfArchived.push(creditPoolId);
      } else if(indexOfArchivedId >= 0) {
        state.idsListOfArchived.splice(indexOfArchivedId, 1);
        state.idsListOfActive.push(creditPoolId);
      }
    },
    [fetchCreditPoolById?.fulfilled.type]: (state, action: PayloadAction<ICreditPool>) => {
      const { isActive = false } = action.payload;
      state.currentView = isActive ? CreditsPoolView.Active : CreditsPoolView.Archived;
    }
  }
});

export const {
  changeViewToActive,
  changeViewToArchived
} = creditsPoolsSlice.actions;

export const creditsPoolsReducer = creditsPoolsSlice.reducer;
